import { AfterViewChecked, Directive, ElementRef, inject } from '@angular/core';

/**
 * SD-2674: fades in newly-committed streamed content.
 *
 * The bubble commits streamed text a sentence at a time and re-sets the whole
 * innerHTML on every commit, so two things need reveal treatment:
 *
 * - Block elements beyond the previously-seen count are brand-new paragraphs;
 *   they get `qt-rev` and fade up as a unit.
 * - The previous tail block usually just grew by a sentence. Re-rendering
 *   would make that text pop in raw, so the appended suffix is wrapped in
 *   `span.qt-rev-txt` and fades in place — only the new words animate while
 *   everything already on screen stays perfectly still.
 */
@Directive({
  selector: '[qtStreamReveal]',
  standalone: true,
})
export class StreamRevealDirective implements AfterViewChecked {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private revealedCount = 0;
  private lastBlockText = '';

  ngAfterViewChecked(): void {
    const children = this.host.nativeElement.children;

    // Settled content — the final event superseding the stream, a history
    // load, a user bubble — must render still. Without this gate the final
    // repaint re-marks every block and the whole finished answer flashes
    // once more. The bubble sets the attribute only while the turn streams.
    if (!this.host.nativeElement.hasAttribute('data-qt-streaming')) {
      this.syncBookkeeping(children);
      return;
    }

    // Content was replaced wholesale mid-stream — start over rather than
    // skipping the rebuilt blocks.
    if (children.length < this.revealedCount) {
      this.revealedCount = 0;
      this.lastBlockText = '';
    }

    // The previously-last block may have grown by a sentence — fade in only
    // its appended text. A non-prefix rewrite (markdown restructured the
    // block) skips the animation instead of guessing what changed.
    if (this.revealedCount > 0) {
      const tail = children[this.revealedCount - 1];
      const text = tail?.textContent ?? '';
      if (
        tail &&
        text.length > this.lastBlockText.length &&
        text.startsWith(this.lastBlockText)
      ) {
        this.wrapAppendedText(tail, text.length - this.lastBlockText.length);
      }
    }

    for (let i = this.revealedCount; i < children.length; i++) {
      children[i].classList.add('qt-rev');
    }

    this.syncBookkeeping(children);
  }

  private syncBookkeeping(children: HTMLCollection): void {
    this.revealedCount = children.length;
    this.lastBlockText =
      children.length > 0
        ? (children[children.length - 1].textContent ?? '')
        : '';
  }

  /**
   * Wraps the last `charCount` characters of `block`'s text in
   * `span.qt-rev-txt`. Walks text nodes from the end so markup boundaries
   * (bold, links, list items) are respected; a node straddling the boundary
   * is split so already-visible words stay still.
   */
  private wrapAppendedText(block: Element, charCount: number): void {
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      nodes.push(n as Text);
    }

    let remaining = charCount;
    for (let i = nodes.length - 1; i >= 0 && remaining > 0; i--) {
      let node = nodes[i];
      const length = node.data.length;
      if (length === 0) continue;
      if (length > remaining) {
        node = node.splitText(length - remaining);
      }
      remaining -= node.data.length;

      const span = document.createElement('span');
      span.className = 'qt-rev-txt';
      node.parentNode?.insertBefore(span, node);
      span.appendChild(node);
    }
  }
}
